// Copied verbatim from original/src/components/StatCard.tsx (ADR-08 §2.1). Changes: StatCardProps
// inlined from original/src/types.ts:10-37; the six capital-button strings go through t() (D-055).
import React, { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { X, Maximize2, ArrowUp, ArrowDown, Plus, TrendingDown, TrendingUp } from 'lucide-react';
import { useT } from '../i18n-context.js';

// original/src/types.ts:10-37
export interface StatCardProps {
  key?: string;
  title: string;
  value: string;
  usdValue?: string;
  subValue?: string;
  subTitle?: string;
  icon: React.ReactNode;
  color: string;
  textColor: string;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  isEditing?: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onColSpanChange?: (newSpan: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  index?: number;
  colSpan?: number;
  dashboardIconSize?: 'sm' | 'md' | 'lg' | 'xl';
  onAddCapital?: () => void;
  onWithdrawCapital?: () => void;
  onCapitalizeProfit?: () => void;
  lang?: string;
}

export function StatCard({ 
  title, 
  value, 
  subValue, 
  subTitle, 
  icon, 
  color, 
  textColor, 
  size = 'md', 
  usdValue,
  onClick,
  isEditing,
  onRemove,
  onResize,
  onColSpanChange,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  index = 0,
  colSpan = 1,
  dashboardIconSize = 'md',
  onAddCapital,
  onWithdrawCapital,
  onCapitalizeProfit,
  lang = 'ar'
}: StatCardProps) {
  const t = useT();
  // Mobile-first responsive padding: comfortable, no squishing
  const padding = size === 'sm' 
    ? 'p-3 sm:p-4' 
    : size === 'lg' 
    ? 'p-4 sm:p-6' 
    : 'p-3.5 sm:p-5';
  
  const getIconContainerSizeClass = (dSize: 'sm' | 'md' | 'lg' | 'xl') => {
    switch (dSize) {
      case 'sm': return 'w-8 h-8 sm:w-9 sm:h-9 rounded-xl';
      case 'md': return 'w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl';
      case 'lg': return 'w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl';
      case 'xl': return 'w-11 h-11 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl';
      default: return 'w-9 h-9 sm:w-11 sm:h-11 rounded-xl sm:rounded-2xl';
    }
  };

  const getLucideIconSize = (dSize: 'sm' | 'md' | 'lg' | 'xl') => {
    switch (dSize) {
      case 'sm': return 16;
      case 'md': return 19;
      case 'lg': return 21;
      case 'xl': return 24;
      default: return 19;
    }
  };

  const iconContainerClass = getIconContainerSizeClass(dashboardIconSize);
  const lucideSize = getLucideIconSize(dashboardIconSize);

  const clonedIcon = React.isValidElement(icon) 
    ? React.cloneElement(icon as React.ReactElement<any>, { size: lucideSize }) 
    : icon;

  // Responsive number formatter that cleanly separates digits and unit/currency
  // so that numbers NEVER break mid-digit or overflow, and units display in a dedicated sleek pill
  const renderFormattedValue = (valStr: string) => {
    if (!valStr) return null;
    const parts = valStr.trim().split(' ');
    
    if (parts.length >= 2) {
      const mainNum = parts.slice(0, -1).join(' ');
      const unit = parts[parts.length - 1] ?? '';
      
      const charCount = mainNum.length;
      const digitsOnly = mainNum.replace(/[^\d]/g, '').length;
      
      // Intelligent responsive scale so 6 to 14 digit numbers fit seamlessly without overflow
      let numSizeClass = 'text-base sm:text-lg md:text-xl';
      if (charCount >= 14 || digitsOnly >= 12) {
        numSizeClass = 'text-[12px] sm:text-[13px] md:text-sm';
      } else if (charCount >= 11 || digitsOnly >= 9) {
        numSizeClass = 'text-[13.5px] sm:text-[15px] md:text-base';
      } else if (charCount >= 8 || digitsOnly >= 7) {
        numSizeClass = 'text-[15px] sm:text-base md:text-lg';
      }

      return (
        <div className="flex items-baseline justify-between gap-1 w-full min-w-0">
          <span className={`${numSizeClass} font-black font-mono tabular-nums tracking-tight leading-tight ${textColor} truncate`}>
            {mainNum}
          </span>
          <span className="text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300 font-sans shrink-0 border border-slate-200/60 dark:border-white/5 shadow-2xs">
            {unit}
          </span>
        </div>
      );
    }
    
    const charCount = valStr.length;
    let numSizeClass = 'text-base sm:text-lg md:text-xl';
    if (charCount >= 14) {
      numSizeClass = 'text-[12px] sm:text-[13px] md:text-sm';
    } else if (charCount >= 10) {
      numSizeClass = 'text-[13.5px] sm:text-[15px] md:text-base';
    }

    return (
      <div className="w-full min-w-0">
        <span className={`${numSizeClass} font-black font-mono tabular-nums tracking-tight leading-tight ${textColor} truncate block`}>
          {valStr}
        </span>
      </div>
    );
  };

  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const currentSpan = useRef(colSpan);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    currentSpan.current = colSpan;
    
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    const deltaX = e.clientX - resizeStartX.current;
    const threshold = 100; // pixels per column span change
    const spanChange = Math.round(deltaX / threshold);
    const newSpan = Math.max(1, Math.min(4, currentSpan.current + spanChange));
    
    if (newSpan !== colSpan) {
      onColSpanChange?.(newSpan);
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  return (
    <motion.div 
      whileHover={isEditing ? {} : { y: -3, scale: 1.01 }}
      whileTap={isEditing ? {} : { scale: 0.985 }}
      onClick={isEditing ? undefined : onClick}
      className={`
        bg-white/95 dark:bg-[#12191f]/90 backdrop-blur-2xl ${padding} rounded-2xl sm:rounded-3xl border border-white/90 dark:border-white/10 flex flex-col justify-between transition-all min-h-[148px] sm:min-h-[162px]
        ${onClick && !isEditing ? 'cursor-pointer hover:border-[#f97342]/40 hover:shadow-[0_12px_32px_rgba(249,115,66,0.12)]' : ''} 
        relative overflow-hidden group shadow-[0_4px_20px_-2px_rgba(0,0,0,0.03),_inset_0_1px_1px_0_rgba(255,255,255,0.9)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.5),_inset_0_1px_1px_0_rgba(255,255,255,0.06)]
        ${isEditing ? (index % 2 === 0 ? 'animate-jiggle' : 'animate-jiggle-alt') : ''}
        ${isResizing ? 'ring-2 ring-[#f97342] ring-inset opacity-90 scale-[1.02] z-50' : ''}
      `}
    >
      {/* iOS Style Edit Controls */}
      {isEditing && (
        <>
          <button 
            onClick={(e) => { e.stopPropagation(); onRemove?.(); }}
            className="absolute top-1 right-1 w-6 h-6 bg-rose-500/90 backdrop-blur-sm text-white rounded-full flex items-center justify-center shadow-lg z-50 hover:scale-110 transition-transform"
          >
            <X size={14} strokeWidth={3} />
          </button>
          
          <div className="absolute top-1 left-1 flex flex-col gap-1 z-50">
            <button 
              onClick={(e) => { e.stopPropagation(); onResize?.(); }}
              className="p-1 bg-blue-500/90 backdrop-blur-sm text-white rounded-lg shadow-md hover:bg-blue-600 transition-colors"
              title="Toggle Size"
            >
              <Maximize2 size={10} />
            </button>
            <div className="flex flex-col gap-1">
              <button 
                disabled={isFirst}
                onClick={(e) => { e.stopPropagation(); onMoveUp?.(); }}
                className="p-1 bg-gray-500/90 backdrop-blur-sm text-white rounded-lg shadow-md hover:bg-gray-600 transition-colors disabled:opacity-30"
              >
                <ArrowUp size={10} />
              </button>
              <button 
                disabled={isLast}
                onClick={(e) => { e.stopPropagation(); onMoveDown?.(); }}
                className="p-1 bg-gray-500/90 backdrop-blur-sm text-white rounded-lg shadow-md hover:bg-gray-600 transition-colors disabled:opacity-30"
              >
                <ArrowDown size={10} />
              </button>
            </div>
          </div>

          {/* Drag to Resize Handle */}
          <div 
            onMouseDown={handleResizeStart}
            className="absolute bottom-0 right-0 w-10 h-10 cursor-nwse-resize flex items-end justify-end p-2 z-50 group/resize"
            title="Drag to Resize"
          >
            <div className="w-4 h-4 border-r-4 border-b-4 border-gray-300 dark:border-gray-600 rounded-br-lg group-hover/resize:border-[#f97342] transition-colors" />
          </div>
        </>
      )}

      {/* Top Ambient Glow matching card theme */}
      <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-[#f97342]/8 rounded-full -mr-12 -mt-12 blur-2xl transition-opacity group-hover:opacity-100 opacity-60 duration-700 pointer-events-none" />
      
      {/* Card Header Row: Icon on start, optional USD badge on end */}
      <div className="flex items-center justify-between w-full relative z-10 mb-2 sm:mb-2.5">
        <div className={`${iconContainerClass} ${color} dark:bg-opacity-20 flex items-center justify-center shrink-0 shadow-2xs transition-transform group-hover:scale-105 duration-300 border border-black/5 dark:border-white/5`}>
          {clonedIcon}
        </div>
        {usdValue && (
          <div className="text-[10px] sm:text-xs font-bold text-teal-600 dark:text-teal-400 font-mono bg-teal-500/10 dark:bg-teal-500/15 px-2 py-0.5 rounded-full flex items-center gap-0.5 shrink-0 border border-teal-500/20 shadow-2xs">
            <span>{usdValue}</span>
          </div>
        )}
      </div>
      
      {/* Card Content: Full Width Title & Non-breaking Value */}
      <div className="flex-1 w-full min-w-0 relative z-10 flex flex-col justify-between">
        <div>
          <p className="text-[11px] sm:text-xs font-bold text-slate-500 dark:text-slate-400 leading-snug line-clamp-2 min-h-[2rem] flex items-center mb-1">
            {title}
          </p>
          <div className="mt-0.5 sm:mt-1">
            {renderFormattedValue(value)}
          </div>
        </div>

        {subValue && (
          <div className="mt-2 sm:mt-2.5 pt-2 border-t border-slate-100/80 dark:border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-1 text-[9.5px] sm:text-xs">
            <span className="text-slate-400 dark:text-slate-500 font-medium truncate leading-tight">
              {subTitle}
            </span>
            <span className="font-bold text-slate-700 dark:text-slate-200 font-mono tabular-nums truncate leading-tight shrink-0 sm:text-right">
              {subValue}
            </span>
          </div>
        )}

        {(onAddCapital || onWithdrawCapital || onCapitalizeProfit) && !isEditing && (
          <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-white/5 flex flex-wrap items-center justify-end gap-1.5 relative z-20">
            {onAddCapital && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAddCapital(); }}
                className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[9px] sm:text-[10px] font-black rounded-lg transition-all shadow-xs flex items-center gap-1 cursor-pointer active:scale-95"
                title={t('statcard.add_capital')}
              >
                <Plus size={11} />
                {t('statcard.add')}
              </button>
            )}
            {onWithdrawCapital && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onWithdrawCapital(); }}
                className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-rose-600 hover:bg-rose-700 text-white text-[9px] sm:text-[10px] font-black rounded-lg transition-all shadow-xs flex items-center gap-1 cursor-pointer active:scale-95"
                title={t('statcard.withdraw_capital')}
              >
                <TrendingDown size={11} />
                {t('statcard.withdraw')}
              </button>
            )}
            {onCapitalizeProfit && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onCapitalizeProfit(); }}
                className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[9px] sm:text-[10px] font-black rounded-lg transition-all shadow-xs flex items-center gap-1 cursor-pointer active:scale-95"
                title={t('statcard.capitalize_profit')}
              >
                <TrendingUp size={11} />
                {t('statcard.capitalize')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Hover Line Indicator */}
      {onClick && !isEditing && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
      )}
    </motion.div>
  );
}
